import { useLocalSearchParams } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import type { ClientActionPlan, ClientDetail, ClientEventType } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatText, hasText } from "../format";
import { fleetClientHref } from "../routes";
import { Empty, Field, Section } from "./DetailSection";

/**
 * A client's action plans as Patriot configures them: the event types it
 * inherits through its template chain, the ones it overrides itself (beside the
 * general plan they replace), and any client-wide plan swaps or instructions.
 */
export function ClientActionPlans({ client }: { client: ClientDetail }) {
  const hasTemplate = client.eventTypesTemplates.length > 0;
  // Without a template, every event type is the client's own and nothing is overridden.
  const overrides = hasTemplate ? client.eventTypes.filter((e) => e.definedBy === client.clientNo) : [];
  const overrideCount = overrides.length + client.actionPlanOverrides.length + client.actionPlanInstructions.length;

  return (
    <>
      <Section title="Action plans">
        <Field label="Template">
          {hasTemplate ?
            <View flexDirection="row" flexWrap="wrap" gap="sm" alignItems="baseline">
              {client.eventTypesTemplates.map((template, index) => (
                <View key={template.clientNo} flexDirection="row" gap="sm" alignItems="baseline">
                  {index > 0 && <Muted>→</Muted>}
                  <ClientLink clientNo={template.clientNo} label={template.name ?? template.clientNo} />
                  {template.name === undefined && <Muted>(not copied to a3s yet)</Muted>}
                </View>
              ))}
            </View>
          : client.isTemplate === true ?
            "None — this is a template"
          : "None"}
        </Field>
        <Field label="Event types">
          {`${String(client.eventTypes.length)} in effect, ${String(overrides.length)} set at this client`}
        </Field>
      </Section>

      {hasTemplate && (
        <Section title={`Overrides at this client (${String(overrideCount)})`}>
          {overrideCount === 0 ?
            <Empty>{"Every event type uses its template's action plan."}</Empty>
          : <>
              {overrides.map((eventType) => (
                <Field key={eventType.typeNo} label={`${String(eventType.typeNo)} ${eventType.description ?? ""}`}>
                  <OverrideValue eventType={eventType} />
                </Field>
              ))}
              {client.actionPlanOverrides.map((override) => (
                <Field key={`swap-${String(override.actionPlanOverrideId)}`} label="Plan swap">
                  {`${planLabel(override.actionPlan)} → ${planLabel(override.alternativeActionPlan)}${
                    hasText(override.actionPlanGrouping) ? ` (${override.actionPlanGrouping})` : ""
                  }`}
                </Field>
              ))}
              {client.actionPlanInstructions.map((entry, index) => (
                <Field key={`instructions-${String(index)}`} label={`Instructions: ${planLabel(entry.actionPlan)}`}>
                  {`${formatText(entry.instructions)}${entry.override === true ? " (replaces the plan's own)" : ""}`}
                </Field>
              ))}
            </>
          }
        </Section>
      )}

      <Section title={`Event types (${String(client.eventTypes.length)})`}>
        {client.eventTypes.length === 0 ?
          <Empty>No event types copied for this client or its templates.</Empty>
        : <EventTypesTable client={client} />}
      </Section>
    </>
  );
}

function OverrideValue({ eventType }: { eventType: ClientEventType }) {
  return (
    <Text textStyle="sm/regular" color="text" selectable>
      {planLabel(eventType.actionPlan)}
      <Text textStyle="xs/regular" color="textMuted">
        {eventType.replaces === undefined ?
          "  added at this client"
        : `  replaces ${planLabel(eventType.replaces.actionPlan)} from ${eventType.replaces.definedBy}`}
      </Text>
    </Text>
  );
}

const COLUMNS = [
  { label: "Type", style: "typeNo" },
  { label: "Event", style: "event" },
  { label: "Action plan", style: "plan" },
  { label: "Priority", style: "priority" },
  { label: "From", style: "from" },
] as const;

function EventTypesTable({ client }: { client: ClientDetail }) {
  return (
    <View style={styles.table}>
      <View style={[styles.row, styles.headerRow]}>
        {COLUMNS.map((column) => (
          <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
            {column.label}
          </Text>
        ))}
      </View>
      {client.eventTypes.map((eventType) => {
        const own = eventType.definedBy === client.clientNo;
        return (
          <View key={eventType.typeNo} style={[styles.row, own && client.eventTypesTemplates.length > 0 && styles.own]}>
            <Text textStyle="xs/regular" color="text" style={styles.typeNo} selectable>
              {String(eventType.typeNo)}
            </Text>
            <Text textStyle="xs/regular" color="text" style={styles.event} numberOfLines={2}>
              {formatText(eventType.description)}
            </Text>
            <Text textStyle="xs/regular" color="text" style={styles.plan} numberOfLines={2}>
              {planLabel(eventType.actionPlan)}
            </Text>
            <Text textStyle="xs/regular" color="text" style={styles.priority}>
              {eventType.actionPlan?.priority === undefined ? EMPTY : String(eventType.actionPlan.priority)}
            </Text>
            <View style={styles.from}>
              {own ?
                <Text textStyle="xs/regular" color="text">
                  This client
                </Text>
              : <ClientLink clientNo={eventType.definedBy} label={eventType.definedBy} small />}
            </View>
          </View>
        );
      })}
    </View>
  );
}

/** An action plan by name, or by id where the replica has not copied it yet. */
function planLabel(plan: ClientActionPlan | undefined): string {
  if (plan === undefined) return EMPTY;
  return plan.description ?? `Action plan ${plan.actionPlanId}`;
}

function ClientLink({ clientNo, label, small = false }: { clientNo: string; label: string; small?: boolean }) {
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  return (
    <TenantLink href={fleetClientHref(tenantId, clientNo)} style={styles.inlineLink}>
      <Text textStyle={small ? "xs/regular" : "sm/regular"} style={styles.link}>
        {label}
      </Text>
    </TenantLink>
  );
}

function Muted({ children }: { children: string }) {
  return (
    <Text textStyle="xs/regular" color="textMuted">
      {children}
    </Text>
  );
}

const styles = StyleSheet.create((theme) => ({
  table: {
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.lg,
    overflow: "hidden",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCard },
  // An event type this client sets itself, over its template's.
  own: { backgroundColor: theme.colors.surfaceCard },
  typeNo: { width: 60 },
  event: { flex: 2, minWidth: 140 },
  plan: { flex: 2, minWidth: 140 },
  priority: { width: 60, textAlign: "right" },
  from: { flex: 1, minWidth: 110 },
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
}));
